import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Layout } from './components/Layout.tsx';
import { QueueRoute } from './components/QueueRoute.tsx';
import { Home } from './pages/Home.tsx';
import { useVersionCheck } from './hooks/useVersionCheck.ts';
import { SubscriptionProvider } from './contexts/SubscriptionContext.tsx';
import type { ReactNode, ComponentType, JSX } from 'react';
import { DevPanel } from './dev/DevPanel.tsx';

// --- Lazy-loaded pages ---

const Send = lazy(() => import('./pages/Send.tsx').then(m => ({ default: m.Send })));
const Request = lazy(() => import('./pages/Request.tsx').then(m => ({ default: m.Request })));
const Scan = lazy(() => import('./pages/Scan.tsx').then(m => ({ default: m.Scan })));
const Pay = lazy(() => import('./pages/Pay.tsx').then(m => ({ default: m.Pay })));
const Settings = lazy(() => import('./pages/Settings.tsx').then(m => ({ default: m.Settings })));
const Queue = lazy(() => import('./pages/Queue.tsx').then(m => ({ default: m.Queue })));
const Customer = lazy(() => import('./pages/Customer.tsx').then(m => ({ default: m.Customer })));
const Handles = lazy(() => import('./pages/info/Handles.tsx').then(m => ({ default: m.Handles })));
const HowItWorks = lazy(() => import('./pages/info/HowItWorks.tsx').then(m => ({ default: m.HowItWorks })));
const Fees = lazy(() => import('./pages/info/Fees.tsx').then(m => ({ default: m.Fees })));
const Privacy = lazy(() => import('./pages/info/Privacy.tsx').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/info/Terms.tsx').then(m => ({ default: m.Terms })));
const HandleSharing = lazy(() => import('./pages/info/HandleSharing.tsx').then(m => ({ default: m.HandleSharing })));
const SubAccounts = lazy(() => import('./pages/info/SubAccounts.tsx').then(m => ({ default: m.SubAccounts })));

// --- Lazy route fallback ---

const PageFallback = (): JSX.Element => (
  <div className="page-loading" aria-busy="true" />
);

// --- Persistent EVM wallet wrapper: loads once, never unmounts ---
// EvmShell is lightweight (wagmi only). Non-EVM adapters load lazily on Scan page.

type PersistentWalletProps = Readonly<{ readonly children: ReactNode }>;

// Routes that require EVM wallet provider (wagmi)
const WALLET_ROUTES = new Set(['/send', '/request', '/scan', '/pay', '/settings', '/queue']);

// /i/<intent> is the merchant's connection QR opened from any camera — it renders Pay
const needsWalletNow = (): boolean =>
  WALLET_ROUTES.has(window.location.pathname) || window.location.pathname.startsWith('/i/');

const PersistentWallet = ({ children }: PersistentWalletProps): JSX.Element => {
  const [Shell, setShell] = useState<ComponentType<{ children: ReactNode }> | null>(null);

  useEffect(() => {
    const load = (): void => {
      import('./wallet/EvmShell.tsx').then(m => setShell(() => m.default));
    };

    // Wallet pages need EVM provider immediately — defer only on homepage/info
    if (needsWalletNow()) {
      load();
      return;
    }

    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(load);
      return () => cancelIdleCallback(id);
    }
    // Fallback: load after 2s
    const timer = setTimeout(load, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Wallet routes: block render until Shell loads (wagmi hooks crash without provider)
  // Non-wallet routes: render immediately, useWallet() returns safe defaults
  if (!Shell) {
    return needsWalletNow() ? <PageFallback /> : <>{children}</>;
  }
  return <Shell>{children}</Shell>;
};

// --- App ---

export const App = (): JSX.Element => {
  useVersionCheck();

  return (
    <PersistentWallet>
      <SubscriptionProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="/send" element={<Suspense fallback={<PageFallback />}><Send /></Suspense>} />
            <Route path="/request" element={<Suspense fallback={<PageFallback />}><Request /></Suspense>} />
            <Route path="/scan" element={<Suspense fallback={<PageFallback />}><Scan /></Suspense>} />
            <Route path="/pay" element={<Suspense fallback={<PageFallback />}><Pay /></Suspense>} />
            <Route path="/i/:id" element={<Suspense fallback={<PageFallback />}><Pay /></Suspense>} />
            <Route path="/settings" element={<Suspense fallback={<PageFallback />}><Settings /></Suspense>} />
            <Route path="/queue" element={<Suspense fallback={<PageFallback />}><QueueRoute><Queue /></QueueRoute></Suspense>} />
            <Route path="/customer" element={<Suspense fallback={<PageFallback />}><Customer /></Suspense>} />
            <Route path="/info/handles" element={<Suspense fallback={<PageFallback />}><Handles /></Suspense>} />
            <Route path="/info/how-it-works" element={<Suspense fallback={<PageFallback />}><HowItWorks /></Suspense>} />
            <Route path="/info/fees" element={<Suspense fallback={<PageFallback />}><Fees /></Suspense>} />
            <Route path="/info/privacy" element={<Suspense fallback={<PageFallback />}><Privacy /></Suspense>} />
            <Route path="/info/terms" element={<Suspense fallback={<PageFallback />}><Terms /></Suspense>} />
            <Route path="/info/handle-sharing" element={<Suspense fallback={<PageFallback />}><HandleSharing /></Suspense>} />
            <Route path="/info/sub-accounts" element={<Suspense fallback={<PageFallback />}><SubAccounts /></Suspense>} />
          </Route>
        </Routes>
        <DevPanel />
      </BrowserRouter>
      </SubscriptionProvider>
    </PersistentWallet>
  );
};
